import { createElement } from "react";
import styles from "./text.module.css";

const paragraphSizes = [40, 32, 24, 20, 18, 16, 14, 12, 10];

const paragraphViews = paragraphSizes.reduce((acc, size) => {
    acc[`p-${size}`] = {
        fontSize: `${size}px`,
        fontWeight: 400,
        lineHeight: "1",
    };
    return acc;
}, {});

const textViews = {
    title: {
        fontSize: "24px",
        fontWeight: 700,
        lineHeight: "1.1",
    },
    subtitle: {
        fontSize: "18px",
        fontWeight: 700,
        lineHeight: "1.1",
    },
    heading: {
        fontSize: "16px",
        fontWeight: 700,
        lineHeight: "1",
    },
    body: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1",
    },
    bodyStrong: {
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: "1",
    },
    caption: {
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "1",
    },
    captionStrong: {
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "1",
    },
    tiny: {
        fontSize: "10px",
        fontWeight: 400,
        lineHeight: "1",
    },
    tinyStrong: {
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: "1",
    },
    display: {
        fontSize: "40px",
        fontWeight: 900,
        lineHeight: "1",
    },
    ...paragraphViews,
};

const legacyVariantToView = {
    h1: "title",
    h2: "subtitle",
    h3: "heading",
    body: "body",
    bodyStrong: "bodyStrong",
    "body-strong": "bodyStrong",
    caption: "caption",
    captionStrong: "captionStrong",
    "caption-strong": "captionStrong",
    tiny: "tiny",
    tinyStrong: "tinyStrong",
    "tiny-strong": "tinyStrong",
    display: "display",
};

const resolveView = (view) => legacyVariantToView[view] || view;

const toPx = (value) => (typeof value === "number" ? `${value}px` : value);
const hasValue = (value) => value !== undefined && value !== null;

export const Text = ({
    tag,
    view,
    as,
    variant,
    className,
    style,
    size,
    weight,
    lineHeight,
    color,
    align,
    transform,
    truncate = false,
    clamp,
    children,
    ...props
}) => {
    const textTag = tag || as || "span";
    const textView = resolveView(view || variant || "body");
    const viewStyle = textViews[textView] || textViews.body;

    const textStyle = {
        "--text-margin": "0",
        ...(!hasValue(size) && hasValue(viewStyle.fontSize) ? { "--text-font-size": toPx(viewStyle.fontSize) } : {}),
        ...(!hasValue(weight) && hasValue(viewStyle.fontWeight) ? { "--text-font-weight": viewStyle.fontWeight } : {}),
        ...(!hasValue(lineHeight) && hasValue(viewStyle.lineHeight) ? { "--text-line-height": String(viewStyle.lineHeight) } : {}),
        ...(hasValue(size) ? { fontSize: toPx(size) } : {}),
        ...(hasValue(weight) ? { fontWeight: weight } : {}),
        ...(hasValue(lineHeight) ? { lineHeight: String(lineHeight) } : {}),
        ...(hasValue(color) ? { color } : {}),
        ...(hasValue(align) ? { textAlign: align } : {}),
        ...(hasValue(transform) ? { textTransform: transform } : {}),
        ...(truncate
            ? {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }
            : {}),
        ...(clamp
            ? {
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: clamp,
                lineClamp: clamp,
            }
            : {}),
        ...style,
    };

    const textClassName = [styles.textBase, className].filter(Boolean).join(" ");

    return createElement(
        textTag,
        {
            ...props,
            className: textClassName,
            style: textStyle,
        },
        children,
    );
};